<!--@format-->
<template>
    <div class="graphical-email-template-widget">
        <div class="widget-header">
            <h3>{{ title }}</h3>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <loading-card v-if="isLoading" />
        <div v-else class="content-wrapper">
            <div class="widget-container">
                <starting-step
                    v-if="currentStep === 'startingPoint'"
                    :template-options="templateOptions"
                    :selected-template-option="selectedTemplateOption"
                    @input="handleInputTemplateOption"
                />
                <existing-graphical-template-step
                    v-if="currentStep === 'existingTemplate'"
                    :clientId="clientId"
                    :agencyId="agencyId"
                    :isOrgUser="isOrgUser"
                    :selectedTemplate="template"
                    @selected-item="handleTemplateSelection"
                    @remove-item="handleRemovedItem"
                />
                <previously-sent-campaign-step
                    v-if="currentStep === 'previouslySentCampaign' && isClientPage"
                    :clientId="clientId"
                    :selectedTemplate="template"
                    @selected-item="handleTemplateSelection"
                    @remove-item="handleRemovedItem"
                />
                <settings-step
                    v-if="currentStep === 'settings' || currentStep === 'edit'"
                    @update-category="handleAddCategory($event)"
                    :account-id="accountId"
                    :template="template"
                />
            </div>
        </div>
        <div class="widget-footer">
            <div class="actions-container">
                <app-button class="transparent-btn" @click="closeWidget"> Cancel </app-button>
            </div>
            <div class="actions-container">
                <app-button v-if="hasBackAction" class="secondary-btn" @click="goBack">
                    <span class="fa fa-arrow-left"></span>
                    {{ backLabel }}
                </app-button>
                <app-button @click="goNext" :disabled="templateDisabled">
                    {{ nextLabel }}
                    <span class="fa fa-arrow-right"></span>
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
import EventBus from '@/scripts/event-bus';

const AppButton = () => import('@/components/common/app-button.vue');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const StartingStep = () =>
    import(
        '@/components/sliding-widgets/email-marketing-template-widget/graphical-email-template-widget/starting-step.vue'
    );
const ExistingGraphicalTemplateStep = () =>
    import(
        '@/components/sliding-widgets/email-marketing-template-widget/graphical-email-template-widget/existing-graphical-template-step.vue'
    );
const SettingsStep = () =>
    import(
        '@/components/sliding-widgets/email-marketing-template-widget/graphical-email-template-widget/settings-step.vue'
    );

const PreviouslySentCampaignStep = () =>
    import(
        '@/components/sliding-widgets/email-marketing-template-widget/graphical-email-template-widget/previously-sent-campaign-step.vue'
    );
const LoadingCard = () => import('@/components/common/loading-card');
import BrandingMixin from '@/mixins/branding-mixin';
import { template } from 'lodash';

export default {
    name: 'graphical-email-template-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'starting-step': StartingStep,
        'existing-graphical-template-step': ExistingGraphicalTemplateStep,
        'loading-card': LoadingCard,
        'settings-step': SettingsStep,
        'previously-sent-campaign-step': PreviouslySentCampaignStep,
    },

    mixins: [BrandingMixin],

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: true,

            selectedTemplateOption: null,

            lastActiveStep: null,

            steps: {
                startingPoint: {
                    title: 'Create a Template',
                    actions: {
                        nextAction: {
                            label: 'Next Step',
                            callback: () => {
                                this.goToStep(this.selectedTemplateOption.value);
                            },
                        },
                    },
                },

                existingTemplate: {
                    title: 'Select a Template',
                    actions: {
                        nextAction: {
                            label: 'Next Step',
                            callback: () => {
                                this.goToStep('settings');
                            },
                        },

                        backAction: {
                            label: 'Go Back',
                            callback: () => {
                                this.form = {
                                    ...this.defaultForm,
                                };
                                this.goToStep('startingPoint');
                            },
                        },
                    },
                },

                previouslySentCampaign: {
                    title: 'Previously Sent Campaign',
                    actions: {
                        nextAction: {
                            label: 'Next Step',
                            callback: () => {
                                this.goToStep('settings');
                            },
                        },
                        backAction: {
                            label: 'Go Back',
                            callback: () => {
                                this.goToStep('startingPoint');
                            },
                        },
                    },
                },

                settings: {
                    title: 'Create a Template',
                    actions: {
                        nextAction: {
                            label: 'Next Step',
                            callback: () => {
                                this.postTemplate().then(() => {
                                    this.closeWidget();
                                    this.$router.push(
                                        `/Templates/${this.templateId}/Edit${this.isFromStudio ? '?fromStudio=true' : ''}`,
                                    );
                                });
                            },
                        },

                        backAction: {
                            label: 'Go Back',
                            callback: () => {
                                this.goToStep(this.lastActiveStep);
                            },
                        },
                    },
                },

                edit: {
                    title: 'Edit Template',
                    actions: {
                        nextAction: {
                            label: 'Next Step',
                            callback: () => {
                                this.patchTemplate().then(() => {
                                    this.closeWidget();
                                    this.$router.push(
                                        `/Templates/${this.templateId}/Edit${this.isFromStudio ? '?fromStudio=true' : ''}`,
                                    );
                                });
                            },
                        },
                    },
                },
            },

            template: {
                name: '',
                category: [],
                configuration: this.$helpers.getDefaultGraphicalConfiguration(),
                lastUpdate: null,
                lastSavedConfiguration: null,
                templateId: null,
            },

            currentStep: 'startingPoint',

            accountSettings: {
                backgroundColor: '#d8e2ee',
                backgroundFontColor: '#141414',
                canvasColor: '#ffffff',
                canvasFontColor: '#000000',
                primaryColor: '#000fb9',
                primaryFontColor: '#ffffff',
                secondaryColor: '#d5d5d5',
                secondaryFontColor: '#212529',
                buttonColor: '#ed7e33',
                buttonFontColor: '#ffffff',
                logoImageUrl: '',
                footer: 'Here is where your address would go.',
            },
        };
    },

    computed: {
        isFromStudio() {
            return this.widget.options ? this.widget.options.isFromStudio : false;
        },

        title() {
            return this.steps[this.currentStep].title;
        },

        isClientPage() {
            return this.$store.state.context.client.id;
        },

        templateOptions() {
            const options = [
                {
                    imageUrl: require('@/assets/images/existing-template.png'),
                    name: 'Existing Template',
                    value: 'existingTemplate',
                },
                {
                    imageUrl: require('@/assets/images/start-fresh.png'),
                    name: 'Start Fresh',
                    value: 'settings',
                },
            ];

            if (this.isClientPage) {
                options.unshift({
                    imageUrl: require('@/assets/images/landing-page-previews.png'),
                    name: 'Previously Sent Campaign',
                    value: 'previouslySentCampaign',
                });
            }

            return options;
        },

        hasBackAction() {
            return this.steps[this.currentStep].actions.backAction !== undefined;
        },

        goBack() {
            return this.steps[this.currentStep].actions.backAction.callback;
        },

        goNext() {
            return this.steps[this.currentStep].actions.nextAction.callback;
        },

        backLabel() {
            return this.steps[this.currentStep].actions.backAction.label;
        },

        nextLabel() {
            return this.steps[this.currentStep].actions.nextAction.label;
        },

        accountId() {
            return this.$store.state.context.account.id;
        },

        clientId() {
            return this.widget.clientId;
        },

        agencyId() {
            return this.widget.agencyId;
        },

        templateDisabled() {
            return (
                (this.currentStep === 'existingTemplate' ||
                    this.currentStep === 'previouslySentCampaign') &&
                !this.template.id
            );
        },

        isOrgUser() {
            return !this.clientId && !this.agencyId;
        },
    },

    created() {
        if (this.widget.templateId) {
            this.templateId = this.widget.templateId;
            this.getTemplate().then(() => {
                this.currentStep = 'edit';
            });
        } else {
            this.isLoading = false;
        }
        this.getAccountSettings();
        this.selectedTemplateOption = this.templateOptions[0];
    },

    methods: {
        handleInputTemplateOption(newTemplateSelected) {
            this.template = {
                name: '',
                category: [],
                configuration: this.$helpers.getDefaultGraphicalConfiguration(),
                lastUpdate: null,
                lastSavedConfiguration: null,
                templateId: null,
            };
            this.selectedTemplateOption = newTemplateSelected;
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        goToStep(step) {
            this.lastActiveStep = this.currentStep;
            this.currentStep = step;
        },

        handleTemplateSelection(selectedOptions) {
            this.template = selectedOptions[0];
            var configuration = this.template.configuration;
            configuration = this.$helpers.parseConfiguration(configuration, null, this.client);

            this.template.configuration = configuration;
        },

        postTemplate() {
            return new Promise((resolve, reject) => {
                this.isLoading = true;

                var stringifiedConfiguration = JSON.stringify(this.template.configuration);

                var request = {
                    Name: this.template.name || 'Template Name',
                    Categories: this.template.category,
                    HasBranding: this.hasGraphicalEmailBranding(this.template.configuration),
                    Configuration: stringifiedConfiguration,
                    ClientId: this.clientId,
                    AgencyId: this.agencyId,

                    PreviewImageUrl:
                        'https://cdn.honeycrm.com/app/Email-Template-Preview-Placeholder.png',
                };

                this.$api
                    .post('/api/Templates', request)
                    .then((response) => {
                        this.templateId = response.data.Id;
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while creating your template.',
                            { error },
                        );
                        reject();
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            });
        },

        patchTemplate() {
            return new Promise((resolve, reject) => {
                this.isLoading = true;

                var changes = [
                    {
                        name: 'Name',
                        value: this.template.name || 'Template Name',
                    },
                    {
                        name: 'Categories',
                        value: this.template.category,
                    },
                ];

                this.$api
                    .patch(`/api/Templates/${this.templateId}`, changes)
                    .then(() => {
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred saving your template.',
                            {
                                error,
                            },
                        );
                        reject();
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            });
        },

        getAccountSettings() {
            return new Promise((resolve, reject) => {
                if (!this.clientId) {
                    resolve();
                } else {
                    this.$api
                        .get(`/service/Clients?$filter=Id eq ${this.clientId}`)
                        .then((response) => {
                            this.client = response.data.value[0];
                            this.accountSettings.logoImageUrl =
                                response.data.value[0].TemplateLogoImageUrl ||
                                this.accountSettings.logoImageUrl;
                            this.accountSettings.backgroundColor =
                                response.data.value[0].TemplateBackgroundColor ||
                                this.accountSettings.backgroundColor;
                            this.accountSettings.backgroundFontColor =
                                response.data.value[0].TemplateBackgroundFontColor ||
                                this.accountSettings.backgroundFontColor;
                            this.accountSettings.primaryColor =
                                response.data.value[0].TemplatePrimaryColor ||
                                this.accountSettings.primaryColor;
                            this.accountSettings.primaryFontColor =
                                response.data.value[0].TemplatePrimaryFontColor ||
                                this.accountSettings.primaryFontColor;
                            this.accountSettings.secondaryColor =
                                response.data.value[0].TemplateSecondaryColor ||
                                this.accountSettings.secondaryColor;
                            this.accountSettings.secondaryFontColor =
                                response.data.value[0].TemplateSecondaryFontColor ||
                                this.accountSettings.secondaryFontColor;
                            this.accountSettings.canvasColor =
                                response.data.value[0].TemplateCanvasColor ||
                                this.accountSettings.canvasColor;
                            this.accountSettings.canvasFontColor =
                                response.data.value[0].TemplateCanvasFontColor ||
                                this.accountSettings.canvasFontColor;
                            this.accountSettings.buttonColor =
                                response.data.value[0].TemplateButtonColor ||
                                this.accountSettings.buttonColor;
                            this.accountSettings.buttonFontColor =
                                response.data.value[0].TemplateButtonFontColor ||
                                this.accountSettings.buttonFontColor;
                            this.accountSettings.footer =
                                response.data.value[0].TemplateFooter ||
                                this.accountSettings.footer;

                            resolve();
                        })
                        .catch(() => {
                            reject();
                        });
                }
            });
        },

        getTemplate() {
            this.isLoading = true;

            return new Promise((resolve, reject) => {
                this.$api
                    .get(
                        '/service/Templates?$expand=Categories&$filter=Id eq ' +
                            this.widget.templateId,
                    )
                    .then((response) => {
                        this.template.name = response.data.value[0].Name;
                        this.template.category = response.data.value[0].Categories.map(
                            ({ Name }) => Name,
                        );

                        resolve();
                    })
                    .catch(reject)
                    .finally(() => {
                        this.isLoading = false;
                    });
            });
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        handleAddCategory(categories) {
            this.template.category = categories;
        },

        handleRemovedItem() {
            this.template = {
                name: '',
                category: [],
                configuration: this.$helpers.getDefaultGraphicalConfiguration(),
                lastUpdate: null,
                lastSavedConfiguration: null,
                templateId: null,
            };
        },
    },
};
</script>

<style>
.graphical-email-template-widget .widget-footer .transparent-btn {
    background: transparent;
    border: 1px solid #cac6cb;
    color: #382153;
}

.graphical-email-template-widget .widget-footer .transparent-btn:hover {
    background: #e1e0e5;
}

.graphical-email-template-widget .widget-footer .actions-container {
    display: flex;
}

.graphical-email-template-widget .widget-footer .secondary-btn {
    background: transparent;
    border: 1px solid#18aeff;
    color: #18aeff;
    margin-right: 14px;
}

.graphical-email-template-widget .widget-footer .secondary-btn:hover {
    background: #dbedfa;
}

.graphical-email-template-widget .widget-footer .secondary-btn span.fa {
    margin-right: 10px;
}

.graphical-email-template-widget .widget-footer .app-button:not(.secondary-btn) span.fa {
    margin-left: 10px;
}
</style>
